import { useTranslation } from 'react-i18next'
import Header from '@/components/Header'
import Back from '@/components/Back'
import PageLayout from '@/components/PageLayout'
import { APP_NAME } from '@/config/constants'

/** 通用翻译插值参数 */
const i18nParams = { appName: APP_NAME }

/** 段落 */
function P({ text, className = '' }: { text: string; className?: string }) {
    return (
        <p className={`text-4 leading-7 text-[#1A1A1A] whitespace-pre-line ${className}`}>
            {text}
        </p>
    )
}

/** 列表项（• key：value 格式） */
function BulletItem({ text }: { text: string }) {
    return (
        <li className="text-4 leading-7 text-[#1A1A1A] flex items-start">
            <span className="mr-1.5 shrink-0">•</span>
            <span>{text}</span>
        </li>
    )
}

/** 带标题的子项 */
function SubItem({ title, text }: { title: string; text: string }) {
    return (
        <div className="mt-4">
            <h4 className="text-4.5 font-bold text-[#1A1A1A] mb-1">{title}</h4>
            <P text={text} />
        </div>
    )
}

/** 一级大标题（一、二、三…） */
function SectionTitle({ text }: { text: string }) {
    return (
        <h2 className="text-7 font-bold text-[#1A1A1A] mt-10 mb-4">
            {text}
        </h2>
    )
}

/** 二级标题（1.1、1.2…） */
function SubSectionTitle({ text }: { text: string }) {
    return <h3 className="text-5 font-bold text-[#1A1A1A] mt-6 mb-2">{text}</h3>
}

/** 功能区块标题（带 emoji） */
function FeatureTitle({ text }: { text: string }) {
    return <h3 className="text-4 font-bold text-[#1A1A1A] mt-8 mb-3">{text}</h3>
}

export default function About() {
    const { t } = useTranslation()
    const T = (key: string) => t(key, i18nParams)

    return (
        <PageLayout>
            <Header
                leftChildren={<Back />}
                centerChildren={<span className='text-3.75 font-medium text-black'>{t('关于我们')}</span>}
            />

            <div className="flex-1 overflow-y-auto px-5 py-6">
                {/* ===== 页面大标题 ===== */}
                <h1 className="text-7.5 font-extrabold text-[#1A1A1A] leading-tight mb-8 mt-0">
                    {T('about_page_title')}
                </h1>

                {/* ===== 品牌介绍 ===== */}
                <div className="mb-6">
                    <div className="text-4 font-medium text-[#1A1A1A] mb-0.5">{T('about_header_brand')}</div>
                    <div className="text-3.5 text-[#666] mb-4">{T('about_header_tagline')}</div>
                    <P text={T('about_header_intro_1')} />
                    <P text={T('about_header_intro_2')} className="mt-3" />
                </div>

                {/* ===== 核心功能 ===== */}
                <FeatureTitle text={T('about_core_title')} />
                <ul className="space-y-1">
                    {[1, 2, 3, 4, 5].map(i => (
                        <BulletItem key={i} text={T(`about_core_item_${i}`)} />
                    ))}
                </ul>

                {/* ===== 合规与安全 ===== */}
                <FeatureTitle text={T('about_compliance_title')} />
                <ul className="space-y-1">
                    {[1, 2, 3, 4].map(i => (
                        <BulletItem key={i} text={T(`about_compliance_item_${i}`)} />
                    ))}
                </ul>

                {/* ===== 典型场景 ===== */}
                <FeatureTitle text={T('about_scenarios_title')} />
                <ul className="space-y-1">
                    {[1, 2, 3, 4].map(i => (
                        <BulletItem key={i} text={T(`about_scenarios_item_${i}`)} />
                    ))}
                </ul>

                {/* ===== 一、项目概述 ===== */}
                <SectionTitle text={T('about_s1_title')} />

                <SubSectionTitle text={T('about_s1_1_title')} />
                <P text={T('about_s1_1_p1')} />
                <P text={T('about_s1_1_p2')} className="mt-3" />
                <P text={T('about_s1_1_p3')} className="mt-3" />

                <SubSectionTitle text={T('about_s1_2_title')} />
                {[1, 2, 3, 4].map(i => (
                    <P key={i} text={T(`about_s1_2_p${i}`)} className={i > 1 ? 'mt-3' : ''} />
                ))}

                <SubSectionTitle text={T('about_s1_3_title')} />
                <P text={T('about_s1_3_intro')} />
                {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                    <SubItem key={i} title={T(`about_s1_3_sub${i}_title`)} text={T(`about_s1_3_sub${i}_text`)} />
                ))}
                <P text={T('about_s1_3_outro')} className="mt-4" />

                <SubSectionTitle text={T('about_s1_4_title')} />
                <P text={T('about_s1_4_intro')} />
                {[1, 2, 3, 4].map(i => (
                    <SubItem key={i} title={T(`about_s1_4_sub${i}_title`)} text={T(`about_s1_4_sub${i}_text`)} />
                ))}
                <P text={T('about_s1_4_outro')} className="mt-4" />

                {/* ===== 二、行业背景与市场分析 ===== */}
                <SectionTitle text={T('about_s2_title')} />

                <SubSectionTitle text={T('about_s2_1_title')} />
                {[1, 2, 3].map(i => (
                    <P key={i} text={T(`about_s2_1_p${i}`)} className={i > 1 ? 'mt-3' : ''} />
                ))}

                <SubSectionTitle text={T('about_s2_2_title')} />
                <P text={T('about_s2_2_intro')} />
                {[1, 2, 3, 4].map(i => (
                    <SubItem key={i} title={T(`about_s2_2_sub${i}_title`)} text={T(`about_s2_2_sub${i}_text`)} />
                ))}

                <SubSectionTitle text={T('about_s2_3_title')} />
                {[1, 2, 3].map(i => (
                    <P key={i} text={T(`about_s2_3_p${i}`)} className={i > 1 ? 'mt-3' : ''} />
                ))}

                <SubSectionTitle text={T('about_s2_4_title')} />
                <P text={T('about_s2_4_intro')} />
                {[1, 2, 3, 4].map(i => (
                    <P key={i} text={T(`about_s2_4_p${i}`)} className="mt-3" />
                ))}

                {/* ===== 三、产品体系详解 ===== */}
                <SectionTitle text={T('about_s3_title')} />

                <SubSectionTitle text={T('about_s3_1_title')} />
                <P text={T('about_s3_1_intro')} />
                <h4 className="text-3.5 font-bold text-[#1A1A1A] mt-4 mb-1">{T('about_s3_1_features_title')}</h4>
                {[1, 2, 3, 4, 5].map(i => (
                    <SubItem key={i} title={T(`about_s3_1_sub${i}_title`)} text={T(`about_s3_1_sub${i}_text`)} />
                ))}

                <SubSectionTitle text={T('about_s3_2_title')} />
                <P text={T('about_s3_2_intro')} />
                {[1, 2, 3, 4, 5].map(i => (
                    <SubItem key={i} title={T(`about_s3_2_sub${i}_title`)} text={T(`about_s3_2_sub${i}_text`)} />
                ))}
                <P text={T('about_s3_2_outro')} className="mt-4" />

                {/* ===== 四、安全与合规体系 ===== */}
                <SectionTitle text={T('about_s4_title')} />

                <SubSectionTitle text={T('about_s4_1_title')} />
                {[1, 2, 3].map(i => (
                    <P key={i} text={T(`about_s4_1_p${i}`)} className={i > 1 ? 'mt-3' : ''} />
                ))}

                <SubSectionTitle text={T('about_s4_2_title')} />
                <P text={T('about_s4_2_p1')} />
                <P text={T('about_s4_2_p2')} className="mt-3" />

                <SubSectionTitle text={T('about_s4_3_title')} />
                {[1, 2, 3].map(i => (
                    <P key={i} text={T(`about_s4_3_p${i}`)} className={i > 1 ? 'mt-3' : ''} />
                ))}

                <SubSectionTitle text={T('about_s4_4_title')} />
                <P text={T('about_s4_4_intro')} />
                {[1, 2, 3, 4, 5].map(i => (
                    <SubItem key={i} title={T(`about_s4_4_sub${i}_title`)} text={T(`about_s4_4_sub${i}_text`)} />
                ))}
                <P text={T('about_s4_4_outro')} className="mt-4" />

                {/* ===== 五、商业模式与盈利逻辑 ===== */}
                <SectionTitle text={T('about_s5_title')} />
                <P text={T('about_s5_intro')} />

                <SubSectionTitle text={T('about_s5_1_title')} />
                <P text={T('about_s5_1_text')} />

                <SubSectionTitle text={T('about_s5_2_title')} />
                <P text={T('about_s5_2_text')} />

                <SubSectionTitle text={T('about_s5_3_title')} />
                <P text={T('about_s5_3_text')} />

                <SubSectionTitle text={T('about_s5_4_title')} />
                <P text={T('about_s5_4_text')} />

                <SubSectionTitle text={T('about_s5_5_title')} />
                <P text={T('about_s5_5_text')} />

                <SubSectionTitle text={T('about_s5_6_title')} />
                <P text={T('about_s5_6_p1')} />
                <P text={T('about_s5_6_p2')} className="mt-3" />
                <ul className="mt-2 space-y-1">
                    {[1, 2, 3, 4].map(i => (
                        <BulletItem key={i} text={T(`about_s5_6_advantage_${i}`)} />
                    ))}
                </ul>
                <P text={T('about_s5_6_outro')} className="mt-4" />

                {/* ===== 六、发展路径 ===== */}
                <SectionTitle text={T('about_s6_title')} />
                <P text={T('about_s6_intro')} />

                <SubSectionTitle text={T('about_s6_1_title')} />
                {[1, 2, 3, 4].map(i => (
                    <SubItem key={i} title={T(`about_s6_1_sub${i}_title`)} text={T(`about_s6_1_sub${i}_text`)} />
                ))}

                <SubSectionTitle text={T('about_s6_2_title')} />
                {[1, 2, 3, 4].map(i => (
                    <SubItem key={i} title={T(`about_s6_2_sub${i}_title`)} text={T(`about_s6_2_sub${i}_text`)} />
                ))}

                <SubSectionTitle text={T('about_s6_3_title')} />
                {[1, 2, 3, 4].map(i => (
                    <SubItem key={i} title={T(`about_s6_3_milestone_${i}_title`)} text={T(`about_s6_3_milestone_${i}_text`)} />
                ))}
                <P text={T('about_s6_outro')} className="mt-4" />

                {/* ===== 七、团队与合作伙伴 ===== */}
                <SectionTitle text={T('about_s7_title')} />

                <SubSectionTitle text={T('about_s7_1_title')} />
                <P text={T('about_s7_1_p1')} />
                <P text={T('about_s7_1_p2')} className="mt-3" />

                <SubSectionTitle text={T('about_s7_2_title')} />
                <P text={T('about_s7_2_p1')} />
                <P text={T('about_s7_2_p2')} className="mt-3" />

                <SubSectionTitle text={T('about_s7_3_title')} />
                {[1, 2, 3, 4].map(i => (
                    <P key={i} text={T(`about_s7_3_p${i}`)} className={i > 1 ? 'mt-3' : ''} />
                ))}

                {/* ===== 八、风险声明与合规提示 ===== */}
                <SectionTitle text={T('about_s8_title')} />

                <SubSectionTitle text={T('about_s8_1_title')} />
                <P text={T('about_s8_1_intro')} />
                {[1, 2, 3, 4].map(i => (
                    <SubItem key={i} title={T(`about_s8_1_sub${i}_title`)} text={T(`about_s8_1_sub${i}_text`)} />
                ))}

                <SubSectionTitle text={T('about_s8_2_title')} />
                {[1, 2, 3].map(i => (
                    <P key={i} text={T(`about_s8_2_p${i}`)} className={i > 1 ? 'mt-3' : ''} />
                ))}

                <SubSectionTitle text={T('about_s8_3_title')} />
                <P text={T('about_s8_3_intro')} />
                {[1, 2, 3, 4, 5].map(i => (
                    <SubItem key={i} title={T(`about_s8_3_sub${i}_title`)} text={T(`about_s8_3_sub${i}_text`)} />
                ))}
                <P text={T('about_s8_3_outro')} className="mt-4" />

                {/* 底部留白 */}
                <div className="h-10" />
            </div>
        </PageLayout>
    )
}
